import styled from 'styled-components';
import WebhookIcon from '@mui/icons-material/Webhook';
import DotLoader from '@svg/dot_loader';

import { useTranslate } from '@common/hooks/useTranslate';
import { Anchor, NodeDataType } from '@components/Flow/Nodes';
import { AnchorType } from '@components/Flow';
import { Command } from '@store/slices';
import { isConnectable, isValidConnection } from '../utils';
import { useSelector } from 'react-redux';
import { State } from '@store/index';
import { InputAnchors } from './NodeAnchors';
import { Tooltip } from '@common/Tooltip';
import { useTheme } from '../../../styles';
import { CloseOutlined } from '@mui/icons-material';

const Container = styled.div<{ hasInputs?: boolean; multipleData?: boolean }>`
  display: grid;
  grid-template-columns: ${({ hasInputs, multipleData }) =>
    hasInputs ? `1fr` : !multipleData ? '1fr 1fr' : '1fr 1.5fr'};
`;

const OutputAnchors = styled.div`
  display: flex;
  align-items: flex-end;
  flex-direction: column;
`;

const OutputAnchorContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  &:not(:last-of-type) {
    border-bottom: 1px solid ${({ theme }) => theme.line.light};
  }
`;

const OutputAnchor = styled.div`
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  padding: 0.75rem 1rem;
`;

const AnchorIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LoaderSVG = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.3rem;
`;

interface Props {
  id: string;
  command: Command;
  data: NodeDataType;
  anchors: AnchorType[];
}

const getWebhookAnchorPairs = (arr: AnchorType[]): AnchorType[][] => {
  const result: AnchorType[][] = [];
  let webhookExecution: AnchorType | null = null;

  for (const item of arr) {
    if (item.type === 'source') {
      if (item.variant === 'webhook_execution') {
        webhookExecution = item;
      } else if (item.variant === 'data' && webhookExecution) {
        result.push([webhookExecution, item]);
        webhookExecution = null;
      }
    }
  }

  return result;
};

export const NodeWebhookAnchors: React.FC<Props> = ({ id, command, data, anchors = [] }) => {
  const theme = useTheme();
  const connections = command.connections || [];

  const trace = useSelector((state: State) => state.tabs.active.run);
  const commands = useSelector((state: State) => state.tabs.active.commands);
  const edges = useSelector((state: State) => state.tabs.active.edges);

  const isRunning = trace?.running;
  const nodeTrace = trace.traces.find((event) => event.metadata?.node_id === command.node.id);
  const nodeWebhooks = isRunning && nodeTrace?.metadata?.webhooks ? nodeTrace.metadata.webhooks || [] : [];
  const nodeCancelledWebhooks = nodeTrace?.metadata?.cancelled_webhooks
    ? nodeTrace.metadata.cancelled_webhooks || []
    : [];

  const { isInfiniteLoop } = useTranslate();

  const dataAnchors = anchors.filter((anchor) => anchor.variant === 'data' && anchor.type === 'source');
  const multipleDataAnchors = dataAnchors.length > 1;

  const sources = anchors.filter((anchor) => anchor.type === 'source');
  const targets = anchors.filter((anchor) => anchor.type === 'target');

  const webhookPairs = getWebhookAnchorPairs(anchors);
  const hasTargets = Boolean(targets.length);

  return (
    <Container hasInputs={!hasTargets} multipleData={multipleDataAnchors}>
      {hasTargets && (
        <InputAnchors hasInputs={Boolean(targets.length && sources.length)}>
          {targets.map((target) => (
            <Anchor
              key={`${target.id}${id}`}
              id={id}
              data={data}
              anchor={target}
              isValidConnection={(connection) => isValidConnection(connection, commands, edges, isInfiniteLoop)}
              isConnectable={isConnectable(target, connections)}
            />
          ))}
        </InputAnchors>
      )}
      <OutputAnchors>
        {webhookPairs.map(([executionAnchor, dataAnchor]) => (
          <OutputAnchorContainer key={`${executionAnchor.id}${id}`}>
            <OutputAnchor>
              <AnchorIcon>
                <Tooltip
                  sx={{ maxWidth: '300px' }}
                  placement='top'
                  title='This is a webhook execution anchor. This means the nodes connected to this anchor will be triggered when this webhook is received after running the command. Webhooks have their own data anchor which represents the payload of the webhook when received.'
                  arrow
                >
                  {nodeCancelledWebhooks.includes(executionAnchor.id) ? (
                    <CloseOutlined sx={{ fontSize: '17px', marginRight: 0.1, color: theme.telnyx.error }} />
                  ) : nodeWebhooks.includes(executionAnchor.id) ? (
                    <LoaderSVG>
                      <DotLoader color={theme.primary} height={3.5} />
                    </LoaderSVG>
                  ) : (
                    <WebhookIcon
                      sx={{ fontSize: '1.3ch', color: theme.text.subtext, '&:hover': { cursor: 'pointer' } }}
                    />
                  )}
                </Tooltip>
                <Anchor
                  id={id}
                  data={data}
                  anchor={executionAnchor}
                  isValidConnection={(connection) => isValidConnection(connection, commands, edges, isInfiniteLoop)}
                  isConnectable={isConnectable(executionAnchor, connections)}
                />
              </AnchorIcon>
              <Anchor
                id={id}
                data={data}
                anchor={dataAnchor}
                isValidConnection={(connection) => isValidConnection(connection, commands, edges, isInfiniteLoop)}
                isConnectable={isConnectable(dataAnchor, connections)}
              />
            </OutputAnchor>
          </OutputAnchorContainer>
        ))}
      </OutputAnchors>
    </Container>
  );
};
